import { useState, useEffect, useRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useAppSelector } from "@/store/hooks";

interface Option {
  id: number | string;
  title: string;
}

interface CustomDropdownProps {
  options: Option[];
  onChange: (selected: Option[]) => void;
  maxSelection?: number;
  className?: string;
  placeholder?: string;
  syncFromProfileCountries?: boolean;
}

const ChooseCountryDropdown = ({
  options,
  onChange,
  maxSelection,
  className,
  placeholder = "Select Interested Countries",
  syncFromProfileCountries = true,
}: CustomDropdownProps) => {
  const [selected, setSelected] = useState<Option[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { userDetails: selectedUserCountryList } = useAppSelector(
    (state) => state.userProfile
  );

  const countries =
    selectedUserCountryList?.student_metadata.interested_countries;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync selected from store (UNCHANGED logic)
  useEffect(() => {
    if (!syncFromProfileCountries) return;
    if (!countries || !options.length) return;

    const selectedFromStore = options.filter((opt) =>
      countries.includes(opt.title)
    );

    setSelected(selectedFromStore);
    onChange(selectedFromStore);
  }, [countries, options, onChange, syncFromProfileCountries]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const isSelected = (id: number | string) =>
    selected.some((s) => s.id === id);

  const toggleSelect = (item: Option, e: React.MouseEvent) => {
    e.stopPropagation();

    const alreadySelected = isSelected(item.id);

    let updated: Option[];

    if (alreadySelected) {
      updated = selected.filter((s) => s.id !== item.id);
    } else {
      // ✅ maxSelection support kept
      if (maxSelection && selected.length >= maxSelection) return;
      updated = [...selected, item];
    }

    setSelected(updated);
    onChange(updated); // ✅ saving logic untouched
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className={`w-full bg-white border-[1.5px] border-black rounded-lg text-black text-base sm:text-lg py-2 px-3 flex justify-between items-center ${className}`}
      >
        <span className="truncate capitalize">
          {selected.length > 0
            ? selected
              .map((s) => s.title.split(" - ").pop())
              .join(", ")
            : placeholder}
        </span>
        <ChevronDown
          className={`ml-2 h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {/* Flat Dropdown */}
      {isOpen && (
        <div className="absolute z-[99] mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-[220px] overflow-auto">
          {options.map((item) => {
            const isItemSelected = isSelected(item.id);
            const countryName = item.title.split(" - ").pop();

            return (
              <div
                key={item.id.toString()}
                className="px-4 py-2 flex items-center hover:bg-gray-100 cursor-pointer"
                onClick={(e) => toggleSelect(item, e)}
              >
                <div className="w-4 h-4 mr-2">
                  {isItemSelected && <Check className="h-4 w-4" />}
                </div>
                <span className="truncate capitalize">
                  {countryName}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChooseCountryDropdown;
